import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { debugLog } from './debug-log.service';
import {
  clearAccessToken,
  getAccessToken,
  getDeviceId,
  setAccessToken,
} from './session-auth.store';

export interface AuthSessionResponse {
  access_token: string;
  expires_in: number;
  user_name?: string;
  cif?: string;
  km_meta?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SessionAuthService {
  private readonly apiUrl = environment.apiUrl;
  private sessionExpired = false;

  getDeviceId(): string {
    return getDeviceId();
  }

  getAccessToken(): string {
    return getAccessToken();
  }

  markSessionExpired(): void {
    if (!this.sessionExpired) {
      debugLog.warn('auth/session', 'Session marked expired; chat requests are blocked until the app is reopened');
    }
    this.sessionExpired = true;
    clearAccessToken();
  }

  isSessionExpired(): boolean {
    return this.sessionExpired;
  }

  async exchangePortalToken(token: string, botConvType?: string): Promise<AuthSessionResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-GFH-Device-Id': getDeviceId(),
    });
    const response = await firstValueFrom(
      this.http.post<AuthSessionResponse>(
        `${this.apiUrl}/auth/session`,
        { token, bot_conv_type: botConvType },
        { headers }
      )
    );
    setAccessToken(response.access_token);
    this.sessionExpired = false;
    const sessionInfo = {
      expiresIn: response.expires_in,
      hasAccessToken: !!response.access_token,
      hasCif: !!response.cif,
      hasUserName: !!response.user_name,
      hasKmMeta: !!response.km_meta,
      kmMetaLength: response.km_meta?.length ?? 0,
      botConvType: botConvType ?? null,
    };
    debugLog.info('auth/session', 'Session established', sessionInfo);
    debugLog.setState('session', { ...sessionInfo, establishedAt: new Date().toISOString() });
    if (!response.km_meta) {
      debugLog.warn('auth/session', 'Session response has no km_meta; the voice agent will not be able to connect');
    }
    return response;
  }

  constructor(private http: HttpClient) {}
}
